<template>
  <div>
    <div v-if="!preview_evaluation">
      <b-form-checkbox
        v-if="allows_crud"
        v-model="limited_words_amount"
        @change="changeLimitWordsAmount"
        switch
      >
        {{ $t("evaluations.newEvaluations.limitAnswerLength") }}.
      </b-form-checkbox>
      <div
        class="limit-container mt-1"
        v-if="limited_words_amount && allows_crud"
      >
        <div class="selector-container">
          <div style="width: 240px">
            {{ $t("evaluations.newEvaluations.limitBy") }}:
            <b-form-select
              v-model="Question.max_answer_length_type"
              :options="maxAnswerLengthType"
              text-field="value"
              value-field="id"
              size="sm"
              class="ml-0"
              style="width: 120px"
              @change="patchQuestionWordAmountType"
            >
            </b-form-select>
          </div>
        </div>
        <div class="input-container">
          <div
            class="answer_type"
            v-if="Question.max_answer_length_type != null"
          >
            <label> {{ $t("evaluations.newEvaluations.setLimit") }}:</label>
            <b-form-input
              type="number"
              size="sm"
              class="max-answer-length ml-2 mb-2"
              v-model.number="Question.max_answer_length"
              @change="patchQuestionWordAmount"
            ></b-form-input>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="preview_evaluation && AnswerLengthType != null">
      <div class="textarea-container mb-0">
        {{
          $t("evaluations.newEvaluations.limitAnswerLengthIn", {
            length: Question.max_answer_length,
            type: AnswerLengthType,
          })
        }}.
      </div>
      <div></div>
    </div>
    <div class="text-here ml-5" v-if="!preview_evaluation">
      <span class="align-self-center noprint">{{
        $t("evaluations.newEvaluations.answerTextHere")
      }}</span>
    </div>
    <div v-else-if="preview_evaluation && !evaluatee_view && !evaluator_view">
      <div class="textarea-container">
        <!-- v-if="Question.new_version" -->
        <ViewContainerRichTextKlv3
          :object_rich_text="Question"
          :content_rich_text="preview_text"
          :valid_state="ValidPreviewText"
          :auto_save="true"
          :permit_blank="true"
          :hide_title="true"
          @save="
            (value) => {
              preview_text = value;
            }
          "
        ></ViewContainerRichTextKlv3>
        <div v-if="!ValidPreviewText" class="is-invalid">
          {{ $t("evaluations.newEvaluations.answerExceedsTeacherLimit") }}.
        </div>
      </div>
    </div>
    <div v-else-if="evaluatee_view && is_running_test && !finished_test">
      <!-- Capture phase: the editor (Quill or the klv3 web component's shadow
           DOM) never sees the paste/drop, so it can't insert external text. -->
      <div
        class="textarea-container"
        @paste.capture="blockExternalInsert"
        @drop.capture="blockExternalInsert"
        @beforeinput.capture="blockInsertFromClipboard"
      >
        <ViewContainerRichTextKlv3
          :object_rich_text="Question"
          :content_rich_text="question_score.answer"
          :valid_state="ValidPreviewText"
          :auto_save="true"
          :permit_blank="true"
          :hide_title="true"
          @save="
            (value) => {
              question_score.answer = value;
              debouncePatchScore();
            }
          "
        ></ViewContainerRichTextKlv3>
        <div v-if="!ValidPreviewText" class="is-invalid">
          <div v-if="question_score.answer.length == 0">
            {{ $t("evaluations.newEvaluations.answerIsEmpty") }}.
          </div>
          <div v-else>
            {{ $t("evaluations.newEvaluations.answerExceedsTeacherLimit") }}.
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="(evaluatee_view && finished_test) || evaluator_view">
      <div class="textarea-container">
        <b-form-checkbox
          v-if="evaluator_view"
          v-model="show_answer_formatting"
          class="noprint mb-1"
          switch
        >
          {{ $t("evaluations.newEvaluations.showAnswerFormatting") }}
          <b-icon
            icon="info-circle"
            class="ml-1"
            v-b-tooltip.top.noninteractive.v-secondary="
              $t('evaluations.newEvaluations.showAnswerFormattingHelp')
            "
          ></b-icon>
        </b-form-checkbox>
        <!-- Plain text lets the evaluator spot answers hidden with white or
             very light colors. -->
        <div
          v-if="evaluator_view && !show_answer_formatting"
          class="custom-disabled-textarea selectable-answer"
          v-text="plainAnswer"
        ></div>
        <RenderVHtml
          v-else
          class="custom-disabled-textarea"
          :class="{ 'selectable-answer': evaluator_view }"
          :content="question_score.answer"
        ></RenderVHtml>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import * as names from "@/store/names";
import { generateUniqueId, toast } from "@/utils/utils";

export default {
  name: "LimitQuestion",
  components: {
    // NewRichTextEditor: () => import("@/components/reusable/NewRichTextEditor"),
    ViewContainerRichTextKlv3: () =>
      import("@/components/reusable/ViewContainerRichTextKlv3"),
    RenderVHtml: () => import("@/components/reusable/RenderVHtml"),
  },
  props: {
    Question: {
      type: Object,
      required: true,
    },
    QuestionScore: {
      type: Object,
      default: function () {
        return {
          id: generateUniqueId(),
          answer: "",
          evaluatee: null,
          redaction_question: this.Question.id,
          chosen_rubric: null,
          attachments: [],
        };
      },
    },
    allows_crud: {
      type: Boolean,
      required: true,
    },
    preview_evaluation: {
      type: Boolean,
      required: true,
    },
    evaluatee_view: {
      type: Boolean,
      default: false,
    },
    evaluator_view: {
      type: Boolean,
      default: false,
    },
    is_running_test: {
      type: Boolean,
      default: false,
    },
    finished_test: {
      type: Boolean,
      default: false,
    },
    // para mostrar el resultado del evaluado al profesor.
    user_id: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      limited_words_amount: false,
      preview_text: "",
      question_score: { ...this.QuestionScore },
      show_answer_formatting: false,
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
      max_answer_length_types: names.REDACTION_QUESTION_LENGTH_ANSWERS,
    }),
    maxAnswerLengthType() {
      let max_answer_length_types = [...this.max_answer_length_types];
      max_answer_length_types.push({
        id: null,
        value: this.$t("reusable.common.notApplicable"),
      });
      return max_answer_length_types;
    },
    plainAnswer() {
      return this.$htmlToPlainText(this.question_score.answer || "", true);
    },
    AnswerLengthType() {
      const type = this.max_answer_length_types.find(
        (x) => x.id == this.Question.max_answer_length_type
      );
      if (type) return type.value;
      else return null;
    },
    ValidPreviewText() {
      if (this.AnswerLengthType != null) {
        let max_answer_length = 0;
        if (this.Question && this.Question.max_answer_length)
          max_answer_length = this.Question.max_answer_length;
        if (this.preview_evaluation && !this.evaluatee_view) {
          if (this.$equals(this.AnswerLengthType, "Párrafos")) {
            const parrafos = this.$htmlToPlainText(this.preview_text, true)
              .split("\n")
              .filter((para) => para.trim() !== "");
            if (parrafos.length <= max_answer_length) return true;
            else return false;
          } else if (this.$equals(this.AnswerLengthType, "Caracteres")) {
            const caracteres = this.$htmlToPlainText(
              this.preview_text,
              true
            ).replace(/\s/g, "").length;
            if (caracteres <= max_answer_length) return true;
            else return false;
          } else if (this.$equals(this.AnswerLengthType, "Palabras")) {
            const palabras = this.$htmlToPlainText(this.preview_text, true)
              .split(/\s+/)
              .filter((word) => word !== "").length;
            if (palabras <= max_answer_length) return true;
            else return false;
          }
        } else if (this.evaluatee_view) {
          if (
            !isNaN(this.question_score.id) &&
            this.question_score.answer.trim() == ""
          )
            return false;
          else if (this.$equals(this.AnswerLengthType, "Párrafos")) {
            const parrafos = this.$htmlToPlainText(
              this.question_score.answer,
              true
            )
              .split("\n")
              .filter((para) => para.trim() !== "");
            if (parrafos.length <= max_answer_length) return true;
            else return false;
          } else if (this.$equals(this.AnswerLengthType, "Caracteres")) {
            const caracteres = this.$htmlToPlainText(
              this.question_score.answer,
              true
            ).replace(/\s/g, "").length;
            if (caracteres <= max_answer_length) return true;
            else return false;
          } else if (this.$equals(this.AnswerLengthType, "Palabras")) {
            const palabras = this.$htmlToPlainText(
              this.question_score.answer,
              true
            )
              .split(/\s+/)
              .filter((word) => word !== "").length;
            if (palabras <= max_answer_length) return true;
            else return false;
          }
        }
      } else {
        if (
          !isNaN(this.question_score.id) &&
          this.question_score.answer.trim() == ""
        )
          return false;
        return true;
      }
      return true;
    },
  },
  methods: {
    blockExternalInsert(event) {
      event.preventDefault();
      event.stopPropagation();
      toast(this.$t("evaluations.newEvaluations.answerPasteNotAllowed"));
    },
    // Covers mobile keyboards and browser menus that insert without a paste event.
    blockInsertFromClipboard(event) {
      if (["insertFromPaste", "insertFromDrop"].includes(event.inputType)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    patchQuestion(item) {
      if (this.Question.question_type == 1) {
        const payload = {
          new_redaction_question_id: this.Question.id,
          item: item,
        };
        this.$store.dispatch(names.PATCH_NEW_REDACTION_QUESTION, payload);
      }
      toast(this.$t("evaluations.newEvaluations.questionUpdated"));
    },
    changeLimitWordsAmount(value) {
      if (value == false) {
        this.patchQuestion({
          max_answer_length: 0,
          max_answer_length_type: null,
        });
      }
    },
    patchQuestionWordAmountType(value) {
      if (value == 0 || value == null)
        this.patchQuestion({
          max_answer_length: 0,
          max_answer_length_type: null,
        });
      else {
        this.patchQuestion({ max_answer_length_type: value });
      }
    },
    patchQuestionWordAmount(value) {
      if (value >= 0 && value != null && value != "") {
        if (this.timeout2) clearTimeout(this.timeout2);
        this.timeout2 = setTimeout(() => {
          this.patchQuestion({ max_answer_length: value });
        }, 1000);
      }
    },
    debouncePatchScore() {
      if (
        this.ValidPreviewText ||
        (this.question_score && this.question_score.answer == "")
      ) {
        if (this.timeout2) clearTimeout(this.timeout2);
        this.timeout2 = setTimeout(() => {
          if (
            this.ValidPreviewText ||
            (this.question_score && this.question_score.answer == "")
          )
            this.saveQuestionScore();
        }, 1000);
      }
    },
    saveQuestionScore() {
      if (this.question_score.evaluatee == this.user_id) {
        if (isNaN(this.question_score.id)) this.createdQuestionScore();
        else this.updatedQuestionScore();
      }
    },
    createdQuestionScore() {
      this.$store
        .dispatch(names.POST_EVALUATEE_REDACTION_QUESTION, this.question_score)
        .then((response) => {
          this.question_score = response;
          toast(this.$t("evaluations.newEvaluations.savedAnswer"));
        });
    },
    updatedQuestionScore() {
      const payload = {
        evaluatee_redaction_question_id: this.question_score.id,
        item: {
          answer: this.question_score.answer,
        },
      };
      this.$store
        .dispatch(names.PATCH_EVALUATEE_REDACTION_QUESTION, payload)
        .then((response) => {
          this.question_score = response;
          toast(this.$t("evaluations.newEvaluations.savedAnswer"));
        });
    },
  },
  watch: {},
  mounted() {
    if (isNaN(this.question_score.id) && this.user_id != null)
      this.question_score.evaluatee = this.user_id;
  },
  created() {
    if (
      this.Question.question_type == 1 &&
      this.Question.max_answer_length_type != null
    )
      this.limited_words_amount = true;
  },
};
</script>

<style scoped>
.rich-text-editor-scroll >>> .ql-editor {
  max-height: 300px;
  overflow: auto;
}
.custom-disabled-textarea {
  background-color: #e9ecef; /* Color de fondo deshabilitado */
  color: #6c757d; /* Color del texto */
  border: 1px solid #ced4da; /* Borde similar al input */
  border-radius: 0.25rem; /* Borde redondeado como el textarea */
  padding: 0.375rem 0.75rem; /* Espaciado interno */
  min-height: 100px; /* Altura mínima como un textarea */
  max-height: 300px;
  overflow: auto !important;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.5;
  cursor: not-allowed; /* Cursor de "no permitido" */
  white-space: pre-wrap; /* Mantiene saltos de línea */
  overflow-y: auto; /* Scroll si el contenido excede */
}
/* Overrides the unselectable-text inherited from NewQuestionCard/BodyInstrument. */
.selectable-answer,
.selectable-answer >>> * {
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
  cursor: text;
}
.is-invalid {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545 !important;
}
.textarea-answer {
  min-height: 7em;
}
.textarea-container {
  margin-bottom: 1em;
  margin-right: 2em;
  margin-left: 1em;
}
.limit-container {
  display: flex;
}
.selector-container {
  display: flex;
}
.input-container {
  display: flex;
}
.max-answer-length {
  width: 90px;
  min-height: 31px;
}
.text-here {
  height: 80px;
  text-align: left;
  margin-left: 1rem;
  display: flex;
}
.answer_type {
  display: flex;
  align-items: center;
}
@media (max-width: 750px) {
  .textarea-container {
    margin-bottom: 1em;
    margin-right: 10px;
    margin-left: 0px;
  }
  .limit-container {
    flex-direction: column;
  }
  .selector-container {
    margin-top: 1rem;
  }
  .input-container {
    margin-top: 1rem;
  }
  label {
    max-width: 140px;
  }
}
@media print {
  .rich-text-editor-scroll >>> .ql-editor {
    max-height: 100%;
  }
  .custom-disabled-textarea {
    max-height: 100%;
  }
  .textarea-answer {
    border: 1px solid #c9c9c9;
  }
  textarea.form-control.is-valid,
  textarea.form-control.is-invalid {
    background-image: none !important;
  }
}
</style>